import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { Password } from 'primereact/password';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { useAuth } from '../context/AuthContext';
import { authService } from '../services/authService';
import './Auth.css';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login, completeTwoFactorLogin } = useAuth();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [twoFactorTempToken, setTwoFactorTempToken] = useState('');
  const [requiresTwoFactor, setRequiresTwoFactor] = useState(false);
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await login(identifier, password);
      if (response.requiresTwoFactor && response.twoFactorTempToken) {
        setRequiresTwoFactor(true);
        setTwoFactorTempToken(response.twoFactorTempToken);
        return;
      }
      const currentUser = authService.getCurrentUser();
      navigate(currentUser?.role === 'END_USER' ? '/tickets' : '/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleTwoFactorSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await completeTwoFactorLogin(twoFactorTempToken, twoFactorCode);
      const currentUser = authService.getCurrentUser();
      navigate(currentUser?.role === 'END_USER' ? '/tickets' : '/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.message || '2FA verification failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card-wrapper">
        <Card className="auth-card">
          <div className="auth-header">
            <img 
              src="/UptimioHelpdesk.png" 
              alt="Uptimio Helpdesk" 
              className="auth-logo-image"
              style={{ 
                height: '80px',
                width: 'auto',
                marginBottom: '1rem'
              }}
            />
            <div className="auth-subtitle">Helpdesk</div>
          </div>

          {error && <Message severity="error" text={error} className="w-full" style={{ marginBottom: '1.5rem' }} />}

          <form onSubmit={requiresTwoFactor ? handleTwoFactorSubmit : handleSubmit} className="auth-form">
            {!requiresTwoFactor ? (
              <>
                <div className="p-field">
                  <label htmlFor="identifier">
                    <i className="pi pi-envelope" style={{ marginRight: '0.5rem', color: '#10b981' }}></i>
                    Email or Username
                  </label>
                  <InputText
                    id="identifier"
                    type="text"
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    placeholder="you@example.com or username"
                    className="w-full"
                    required
                  />
                </div>

                <div className="p-field">
                  <label htmlFor="password">
                    <i className="pi pi-lock" style={{ marginRight: '0.5rem', color: '#10b981' }}></i>
                    Password
                  </label>
                  <Password
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full"
                    toggleMask
                    feedback={false}
                    required
                  />
                </div>
              </>
            ) : (
              <>
                <div className="p-field">
                  <label htmlFor="twoFactorCode">
                    <i className="pi pi-shield" style={{ marginRight: '0.5rem', color: '#10b981' }}></i>
                    {useRecoveryCode ? 'Recovery code' : '2FA Code'}
                  </label>
                  <InputText
                    id="twoFactorCode"
                    value={twoFactorCode}
                    onChange={(e) => setTwoFactorCode(
                      useRecoveryCode
                        ? e.target.value.toUpperCase().slice(0, 9)
                        : e.target.value.replace(/\D/g, '').slice(0, 6)
                    )}
                    placeholder={useRecoveryCode ? 'ABCD-EFGH' : '123456'}
                    className="w-full"
                    required
                    autoFocus
                  />
                  <small style={{ color: '#6b7280' }}>
                    {useRecoveryCode
                      ? 'Enter one of the recovery codes you saved when enabling 2FA.'
                      : 'Enter the 6-digit code from your authenticator app.'}
                  </small>
                  <button
                    type="button"
                    className="auth-link-button"
                    onClick={() => {
                      setUseRecoveryCode(!useRecoveryCode);
                      setTwoFactorCode('');
                      setError('');
                    }}
                  >
                    {useRecoveryCode ? 'Use authenticator code' : 'Lost your phone? Use a recovery code'}
                  </button>
                </div>

                <Button
                  type="button"
                  label="Back"
                  icon="pi pi-arrow-left"
                  className="p-button-secondary w-full"
                  onClick={() => {
                    setRequiresTwoFactor(false);
                    setTwoFactorTempToken('');
                    setTwoFactorCode('');
                    setUseRecoveryCode(false);
                    setError('');
                  }}
                  style={{ marginBottom: '0.75rem' }}
                />
              </>
            )}

            <Button
              label={
                loading
                  ? (requiresTwoFactor ? 'Verifying...' : 'Signing in...')
                  : (requiresTwoFactor ? 'Verify code' : 'Sign In')
              }
              icon={loading ? "pi pi-spin pi-spinner" : (requiresTwoFactor ? "pi pi-shield" : "pi pi-sign-in")}
              type="submit"
              className="w-full"
              loading={loading}
              disabled={loading}
            />

            {/* REGISTRATION HIDDEN - DO NOT DELETE */}
            {/* <div className="auth-footer">
              <p>
                Don't have an account? <Link to="/register">Sign up for free</Link>
              </p>
            </div> */}
          </form>
        </Card>
      </div>
    </div>
  );
};

export default Login; 